import { cn } from '@/shared/lib/utils'
import { Spinner } from '@/ui/icons/spinner'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef } from 'react'

export type ButtonVariant = VariantProps<typeof buttonVariants>['variant']

//TODO: temp add !important to Button styles until refactoring
const buttonVariants = cva(
  cn(
    'cursor-pointer select-none inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'rounded-[70px] text-black font-bold transition-colors focus-visible:outline-none focus-visible:ring-1 disabled:pointer-events-none',
    'active:brightness-75',
    'hover:brightness-75',
  ),
  {
    variants: {
      variant: {
        primary:
          'bg-primary-500 !text-black disabled:bg-gray-900 disabled:!text-white/50',
        secondary: 'bg-white !text-black',
        tertiary:
          'bg-gray-900 !text-white disabled:bg-gray-900 disabled:!text-white/50',
        danger: 'bg-black !text-red-450',
        dangerLight: 'bg-white !text-red-450',
      },
      size: {
        md: 'px-4 py-2 text-x14 h-[42px]',
        lg: 'px-4 py-2 text-x16 h-[54px]',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'lg',
    } as const,
  },
)

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    fullWidth?: boolean
    pending?: boolean
  }

export type ButtonVariants = Exclude<
  VariantProps<typeof buttonVariants>['variant'],
  null | undefined
>

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth = false,
      asChild = false,
      children,
      pending,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          fullWidth ? 'w-full' : 'w-auto',
          className,
        )}
        ref={ref}
        {...props}
      >
        {pending ? <Spinner /> : children}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button }
